import { useState } from 'react'
import type { LeaderboardEntry } from '@/types/scoring'
import { MAX_SCORE } from '@/types/scoring'
import { PlayerPicksModal } from './PlayerPicksModal'

interface LeaderboardTableProps {
  entries: LeaderboardEntry[]
  currentPlayerId?: string
}

export function LeaderboardTable({ entries, currentPlayerId }: LeaderboardTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [viewPicks, setViewPicks] = useState<{ id: string; name: string } | null>(null)

  return (
    <>
    <div className="flex flex-col gap-2">
      {entries.map((entry, i) => {
        const isMe = entry.player_id === currentPlayerId
        const isExpanded = expanded === entry.player_id
        const pct = MAX_SCORE > 0 ? (entry.total_points / MAX_SCORE) * 100 : 0
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null
        const streak = entry.current_streak ?? 0
        const bestStreak = entry.best_streak ?? 0

        return (
          <div
            key={entry.player_id}
            className={`card overflow-hidden transition-all duration-200
              ${isMe ? 'border-emerald-500/50 shadow-emerald-500/10 shadow-md' : ''}
            `}
          >
            <div className="px-4 py-3 flex items-center gap-3">
              {/* Rank */}
              <span className="text-xl w-8 text-center flex-shrink-0">
                {medal ?? <span className="text-sm text-white/40 font-bold">#{entry.rank}</span>}
              </span>

              {/* Name + badges — click opens picks modal */}
              <button
                className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                onClick={() => setViewPicks({ id: entry.player_id, name: entry.display_name })}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold truncate underline-offset-2 hover:underline ${isMe ? 'text-emerald-400' : 'text-white'}`}>
                    {entry.display_name}
                  </span>
                  {entry.is_ai === 1 && <span className="badge-ai">🤖 AI</span>}
                  {isMe && <span className="text-[10px] text-emerald-400/70 font-medium">you</span>}
                  {entry.r32_picks > 0
                    ? <span className="text-[10px] text-emerald-500/60 font-medium">✓ {entry.r32_picks}/32</span>
                    : <span className="text-[10px] text-white/20 font-medium">no picks</span>
                  }
                  {streak >= 2 && (
                    <span className="text-[10px] font-bold text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded-full">
                      🔥 {streak}
                    </span>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden w-full max-w-xs">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-gold-400 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </button>

              {/* Total points */}
              <div className="flex-shrink-0 text-right">
                <div className="text-xl font-black text-gold-400 tabular-nums">{entry.total_points}</div>
                <div className="text-[10px] text-white/30">pts</div>
              </div>

              {/* Expand breakdown toggle */}
              <button
                className="text-white/30 hover:text-white/60 transition-colors p-1"
                onClick={() => setExpanded(isExpanded ? null : entry.player_id)}
              >
                <span className={`inline-block transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
              </button>
            </div>

            {/* Expanded breakdown */}
            {isExpanded && (
              <div className="px-4 pb-3 border-t border-white/10 mt-2 space-y-2">
                <div className="grid grid-cols-6 gap-2 text-center">
                  {[
                    { label: 'R32', pts: entry.r32_pts },
                    { label: 'R16', pts: entry.r16_pts },
                    { label: 'QF', pts: entry.qf_pts },
                    { label: 'SF/3rd', pts: entry.sf_pts },
                    { label: 'Final', pts: entry.final_pts },
                    { label: '🏆 Champ', pts: entry.champion_pts ?? 0 },
                  ].map(({ label, pts }) => (
                    <div key={label} className="bg-white/5 rounded-lg p-2">
                      <div className="text-[10px] text-white/40 mb-1">{label}</div>
                      <div className="text-sm font-bold text-white">{pts}</div>
                    </div>
                  ))}
                </div>
                {/* Streak row */}
                {bestStreak > 0 && (
                  <div className="flex items-center gap-3 bg-orange-500/5 border border-orange-500/20 rounded-lg px-3 py-2 text-xs">
                    <span className="text-orange-400 font-bold">🔥 Champion streak</span>
                    <span className="text-white/60">Current: <span className="text-orange-300 font-bold">{streak}</span></span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/60">Best: <span className="text-orange-300 font-bold">{bestStreak}</span></span>
                    <span className="text-white/40 ml-auto text-[10px]">+{streak >= 2 ? streak * 2 : 0} pts/round</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>

    {viewPicks && (
      <PlayerPicksModal
        playerId={viewPicks.id}
        displayName={viewPicks.name}
        onClose={() => setViewPicks(null)}
      />
    )}
  </>
  )
}
